import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronLeft, faChevronRight, faStar } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarOutline } from '@fortawesome/free-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import PopperMenu from '@/shared/components/menu/popper-menu';
import Toggle from '@/shared/components/toggle/toggle';
import '@/shared/components/menu/menu-list.scss';
import '@/shared/components/checkbox-row/checkbox-row.scss';

/** Left padding for a row at a given nesting depth — shared so MenuListRow, CollapsibleMenuRow, and hook-driven footer content (e.g. ViewModeMenuItems) can't drift out of sync. */
export function menuItemPaddingLeft(depth: number): number {
  return 6 + depth * 24;
}

export interface MenuListItem {
  key: string;
  label: React.ReactNode;
  icon?: IconDefinition;
  /** Custom icon content (e.g. a widget's own icon+variant JSX) rendered instead of `icon`, in the same slot. */
  iconElement?: React.ReactNode;
  selected?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  /** Always rendered indented underneath this item. */
  nested?: MenuListItem[];
  /** Navigates to a new page showing these items, with a back row to return. */
  page?: MenuListItem[];
  /** Renders a horizontal divider instead of a row; all other fields are ignored. */
  divider?: boolean;
  /** Renders as an expand/collapse row (starts collapsed unless `defaultExpanded`); children shown indented once expanded. */
  collapsible?: MenuListItem[];
  /** Starts a `collapsible` group already expanded. Default false. */
  defaultExpanded?: boolean;
  /** Trailing badge (e.g. a selected-count indicator), shown before the chevron/toggle. */
  badge?: string | number;
  /** Custom inline content instead of the standard row (e.g. a slider) — ignores label/icon/selected/onSelect. */
  content?: React.ReactNode;
  /** Renders the row's icon/label in the danger color (e.g. a destructive action). */
  danger?: boolean;
  /** Shows a checkmark (e.g. the current selection in a single-select list) — unlike `selected`, doesn't render a Toggle or affect close-on-click. */
  checked?: boolean;
  /** Shows a filled gold star (e.g. a favorited item). */
  favorite?: boolean;
  /** Makes the favorite star clickable (e.g. to toggle a favorited widget) instead of the plain static indicator. */
  onToggleFavorite?: (e: React.MouseEvent) => void;
  /** Renders `selected` as a checkbox square (matching CheckboxRow) instead of a Toggle switch — for multi-select lists mirroring a desktop checkbox filter panel. */
  checkboxStyle?: boolean;
}

/** An expand/collapse row — the same primitive `MenuListRow` uses for `collapsible` items, exported for custom (non-MenuListItem) content that wants the same look, e.g. a hook-driven footer section. */
export function CollapsibleMenuRow({
  label,
  icon,
  badge,
  depth = 0,
  defaultExpanded = false,
  children,
}: {
  label: React.ReactNode;
  icon?: IconDefinition;
  badge?: string | number;
  depth?: number;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const toggle = () => setExpanded(e => !e);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className="menu-list-item"
        style={{ paddingLeft: menuItemPaddingLeft(depth) }}
        onClick={toggle}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        }}
      >
        {icon && <FontAwesomeIcon icon={icon} className="menu-list-item-icon" />}
        <span className="menu-list-item-label">{label}</span>
        {badge !== undefined && <span className="menu-list-item-badge">{badge}</span>}
        <FontAwesomeIcon
          icon={faChevronRight}
          className={`menu-list-item-chevron${expanded ? ' menu-list-item-chevron-expanded' : ''}`}
        />
      </div>

      {expanded && (
        <div className="menu-list-nested" style={{ marginLeft: depth * 20 }}>
          {children}
        </div>
      )}
    </>
  );
}

function MenuListRow({
  item,
  depth = 0,
  onNavigate,
  close,
}: {
  item: MenuListItem;
  depth?: number;
  onNavigate: (item: MenuListItem) => void;
  close: () => void;
}) {
  if (item.content) {
    return (
      <div className="menu-list-item-content" style={{ paddingLeft: menuItemPaddingLeft(depth) }}>
        {item.content}
      </div>
    );
  }

  if (item.collapsible) {
    return (
      <CollapsibleMenuRow
        label={item.label}
        icon={item.icon}
        badge={item.badge}
        depth={depth}
        defaultExpanded={item.defaultExpanded}
      >
        {item.collapsible.map(child => (
          <MenuListRow
            key={child.key}
            item={child}
            depth={depth + 1}
            onNavigate={onNavigate}
            close={close}
          />
        ))}
      </CollapsibleMenuRow>
    );
  }

  const handleClick = () => {
    if (item.disabled) return;
    if (item.page) {
      onNavigate(item);
      return;
    }
    item.onSelect?.();
    // Plain action items (no toggle state, no page) close the menu once
    // triggered — toggles/checkboxes stay open so they can be multi-selected.
    if (item.selected === undefined) close();
  };

  return (
    <>
      <div
        role="button"
        tabIndex={item.disabled ? undefined : 0}
        className={`menu-list-item${item.disabled ? ' menu-list-item-disabled' : ''}${item.danger ? ' menu-list-item-danger' : ''}`}
        style={{ paddingLeft: menuItemPaddingLeft(depth) }}
        onClick={handleClick}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick();
          }
        }}
      >
        {item.iconElement ? (
          <span className="menu-list-item-icon">{item.iconElement}</span>
        ) : (
          item.icon && <FontAwesomeIcon icon={item.icon} className="menu-list-item-icon" />
        )}
        <span className="menu-list-item-label">{item.label}</span>
        {item.badge !== undefined && <span className="menu-list-item-badge">{item.badge}</span>}
        {item.onToggleFavorite ? (
          <button
            type="button"
            aria-label="Toggle favorite"
            className="menu-list-item-favorite-toggle"
            onClick={e => {
              e.stopPropagation();
              item.onToggleFavorite?.(e);
            }}
          >
            <FontAwesomeIcon icon={item.favorite ? faStar : faStarOutline} />
          </button>
        ) : (
          item.favorite && <FontAwesomeIcon icon={faStar} className="menu-list-item-favorite" />
        )}
        {item.checked && <FontAwesomeIcon icon={faCheck} className="menu-list-item-check" />}
        {item.page ? (
          <FontAwesomeIcon icon={faChevronRight} className="menu-list-item-chevron" />
        ) : item.selected !== undefined ? (
          item.checkboxStyle ? (
            <input
              type="checkbox"
              className="checkbox-row-input"
              checked={item.selected}
              readOnly
              tabIndex={-1}
              style={{ pointerEvents: 'none' }}
            />
          ) : (
            <Toggle checked={item.selected} />
          )
        ) : null}
      </div>

      {item.nested && (
        <div className="menu-list-nested">
          {item.nested.map(child => (
            <MenuListRow
              key={child.key}
              item={child}
              depth={depth + 1}
              onNavigate={onNavigate}
              close={close}
            />
          ))}
        </div>
      )}
    </>
  );
}

function resolvePage(
  items: MenuListItem[],
  path: string[],
): { items: MenuListItem[]; title?: React.ReactNode } {
  let current = items;
  let title: React.ReactNode;

  for (const key of path) {
    const found = current.find(i => i.key === key);
    if (!found?.page) break;
    title = found.label;
    current = found.page;
  }

  return { items: current, title };
}

/**
 * Renders a MenuListItem[] as pages (drill-in `page`s with a back row), with
 * no trigger/popper of its own — the primitive both MenuList (dropdown) and
 * any full-page presentation (e.g. a mobile MobileFullscreenPanel) render.
 */
export function MenuListPages({
  items = [],
  close = () => {},
}: {
  items?: MenuListItem[];
  /** Called by plain action rows once triggered (e.g. to close an ancestor dropdown/panel). Defaults to a no-op for standalone full-page usage. */
  close?: () => void;
}) {
  const [path, setPath] = useState<string[]>([]);
  const { items: currentItems, title } = resolvePage(items, path);

  return (
    <>
      {path.length > 0 && (
        <div
          role="button"
          tabIndex={0}
          className="menu-list-item menu-list-back"
          onClick={() => setPath(p => p.slice(0, -1))}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setPath(p => p.slice(0, -1));
            }
          }}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="menu-list-item-icon" />
          <span className="menu-list-item-label">{title}</span>
        </div>
      )}

      {currentItems.map(item =>
        item.divider ? (
          <hr key={item.key} className="menu-list-divider" />
        ) : (
          <MenuListRow
            key={item.key}
            item={item}
            onNavigate={i => setPath(p => [...p, i.key])}
            close={close}
          />
        ),
      )}
    </>
  );
}

export default function MenuList({
  trigger,
  items = [],
  align = 'left',
  minWidth = 190,
  toggleClassName,
  toggleTitle,
  toggleDisabled,
  footer,
  elevated,
  menuClassName,
  menuStyle,
  matchReferenceWidth,
  onOpen,
}: {
  trigger: React.ReactNode;
  items?: MenuListItem[];
  align?: 'left' | 'right';
  minWidth?: number;
  toggleClassName?: string;
  toggleTitle?: string;
  toggleDisabled?: boolean;
  /** Extra content rendered below the items — for controls that don't fit the MenuListItem shape (e.g. a slider). */
  footer?: React.ReactNode | ((close: () => void) => React.ReactNode);
  /** Render above the navbar and the deep-dive side panel instead of below both — see PopperMenu. Default false. */
  elevated?: boolean;
  /** Extra className appended to the menu panel (e.g. to override its size/position for a tall catalog). */
  menuClassName?: string;
  /** Extra inline styles applied to the menu panel. */
  menuStyle?: React.CSSProperties;
  /** Size the menu panel to match the toggle button's width instead of `minWidth`. */
  matchReferenceWidth?: boolean;
  /** Called when the menu opens — e.g. to reset transient local state (in addition to the page-path reset MenuList already does). */
  onOpen?: () => void;
}) {
  const [resetKey, setResetKey] = useState(0);

  return (
    <PopperMenu
      toggle={trigger}
      toggleClassName={toggleClassName ?? 'btn btn-link btn-sm p-1 py-0'}
      elevated={elevated}
      toggleTitle={toggleTitle}
      toggleDisabled={toggleDisabled}
      align={align}
      minWidth={minWidth}
      matchReferenceWidth={matchReferenceWidth}
      menuClassName={`dropdown-menu show menu-list${menuClassName ? ` ${menuClassName}` : ''}`}
      menuStyle={menuStyle}
      onOpen={() => {
        setResetKey(k => k + 1);
        onOpen?.();
      }}
    >
      {close => (
        <>
          <MenuListPages key={resetKey} items={items} close={close} />
          {typeof footer === 'function' ? footer(close) : footer}
        </>
      )}
    </PopperMenu>
  );
}
